/**
 * ETag-aware fetch for the small, mutable files (`index.json`, `latest.json`,
 * `fleet.json` — requirement 2d: "honour the nginx headers: ... index/
 * latest/fleet are no-cache — poll them with If-None-Match/ETag ... so an
 * unchanged poll costs a 304"), with a network-first-with-cached-fallback
 * policy (requirement 2a) so a transient network blip or an offline reload
 * still shows the last-known state instead of a blank panel.
 *
 * The ETag + last-good body live in IndexedDB (`src/cache/idb.ts`), not
 * memory, specifically so a page refresh can serve the cached body
 * immediately while revalidating in the background (requirement 4:
 * "stale-while-revalidate UI (show cached instantly, update in place,
 * never blank a panel to reload it)").
 */

import { idbGetHttpEntry, idbPutHttpEntry, isIndexedDbAvailable } from "./idb";
import { NotPublishedYetError } from "../data/fetchJson";

export interface CachedFetchResult<T> {
  readonly value: T;
  /** `true` when this came from the network (200 or a fresh fetch);
   * `false` when served from cache (304, or the network attempt failed). */
  readonly fromNetwork: boolean;
  /** `true` on a 304 specifically — the "cost a 304" case in requirement 2d. */
  readonly notModified: boolean;
}

export class CachedFetchError extends Error {}

/** Fetches `url` as JSON with ETag revalidation, falling back to the last
 * cached body on any network failure (including a genuine 404 the *first*
 * time nothing has ever been cached, which still propagates so callers can
 * treat it as "not published yet"). */
export async function fetchJsonWithEtag<T>(
  url: string,
  timeoutMs = 10_000,
): Promise<CachedFetchResult<T>> {
  const canCache = isIndexedDbAvailable();
  const cached = canCache ? await idbGetHttpEntry(url) : undefined;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const headers: Record<string, string> = {};
    if (cached?.etag) headers["If-None-Match"] = cached.etag;
    const response = await fetch(url, { signal: controller.signal, cache: "no-store", headers });

    if (response.status === 304 && cached) {
      return { value: JSON.parse(cached.body) as T, fromNetwork: false, notModified: true };
    }
    if (response.status === 404 || response.status === 403) {
      // A 403 is treated the same as a 404 here — see src/data/fetchJson.ts's
      // docstring for why (verified live against the real host 2026-09-26).
      if (cached)
        return { value: JSON.parse(cached.body) as T, fromNetwork: false, notModified: false };
      throw new NotPublishedYetError(url);
    }
    if (!response.ok) {
      if (cached)
        return { value: JSON.parse(cached.body) as T, fromNetwork: false, notModified: false };
      throw new CachedFetchError(`failed to fetch ${url}: status ${response.status}`);
    }

    const body = await response.text();
    const etag = response.headers.get("etag");
    if (canCache) {
      await idbPutHttpEntry({ url, etag, body, storedAt: Date.now() });
    }
    return { value: JSON.parse(body) as T, fromNetwork: true, notModified: false };
  } catch (error) {
    if (error instanceof NotPublishedYetError || error instanceof CachedFetchError) throw error;
    if (cached) {
      return { value: JSON.parse(cached.body) as T, fromNetwork: false, notModified: false };
    }
    throw error;
  } finally {
    clearTimeout(timer);
  }
}
